import React from "react";
import { BuilderState, ElStyle, Theme } from "./state";
import { EmailBuilderActions } from "./useEmailBuilder";
import { Block } from "../blockModel/types"; // Block still used in BlockPanel / ThemePanel signatures
import { RangeField, SegField, SwatchField, FontField } from "./inspectorControls";
import { SecHead, SecPill, SecSub, ThemeCard, WarnBox } from "./inspectorStyled";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lum(hex: string): number {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
	if (!m) return 0;
	const n = parseInt(m[1], 16);
	const r = ((n >> 16) & 255) / 255;
	const g = ((n >> 8) & 255) / 255;
	const b = (n & 255) / 255;
	const f = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
	return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

const EL_NAME: Record<string, string> = {
	title: "Tiêu đề",
	subtitle: "Phụ đề",
	text: "Nhãn (chip)",
	html: "Đoạn văn",
	rlbl: "Nhãn (bảng)",
	rval: "Giá trị (bảng)",
	rlbl2: "Nhãn cột phải",
	rval2: "Giá trị cột phải",
	rraw: "Tiêu đề mục",
	ntitle: "Tiêu đề hộp",
	nk: "Nhãn ghi chú",
	nv: "Giá trị ghi chú",
	nli: "Dòng ghi chú",
	label: "Chữ nút",
	fline: "Dòng footer",
	note: "Ghi chú footer",
};

const EL_DEFSIZE: Record<string, number> = {
	title: 21,
	subtitle: 12,
	text: 12,
	html: 14,
	rlbl: 11,
	rval: 15,
	ntitle: 13,
	label: 13,
	note: 11,
};

const COLOR_PRESETS = ["#1f2937", "#334155", "#005e9e", "#6b7280", "#a16207", "#067647", "#b91c1c"];
const BG_PRESETS = ["#eaf3fb", "#005e9e", "#fef6e7", "#f1f5f9", "#067647", "#ffffff"];
const BRAND_PRESETS = ["#005e9e", "#0e7490", "#7c3aed", "#be123c", "#15803d"];

// ---------------------------------------------------------------------------
// ElementPanel
// ---------------------------------------------------------------------------

interface ElementPanelProps {
	selEl: string;
	state: BuilderState;
	actions: EmailBuilderActions;
}

export function ElementPanel({ selEl, state, actions }: ElementPanelProps): JSX.Element {
	const cur: ElStyle = state.elStyle[selEl] || {};
	const parts = selEl.split(":");
	const field = parts[1] || "";
	const isPill = field === "text" || field === "label";
	const isSummaryRow = field === "rlbl" || field === "rval";
	const isSummaryRowRight = field === "rlbl2" || field === "rval2";
	const isRaw = field === "rraw";

	const defSize = EL_DEFSIZE[field] ?? 14;
	const elName = EL_NAME[field] || field;

	// Row layout for summary row fields
	let rowLayout = "stacked";
	if (isSummaryRow) {
		const block = state.blocks.find((b) => b.id === parts[0]);
		const rows = block?.data?.rows as Array<{ inline?: boolean }> | undefined;
		const rowIdx = Number(parts[2]);
		rowLayout = rows && rows[rowIdx]?.inline ? "inline" : "stacked";
	}

	// Current emphasis state for emphasis toggle
	const rowIdx = Number(parts[2]);
	const emphasisBlock = isSummaryRow || isSummaryRowRight ? state.blocks.find((b) => b.id === parts[0]) : null;
	const emphasisRows = emphasisBlock?.data?.rows as Array<{ emphasis?: boolean; emphasis2?: boolean }> | undefined;
	const currentEmphasisLeft = emphasisRows ? !!emphasisRows[rowIdx]?.emphasis : false;
	const currentEmphasisRight = emphasisRows ? !!emphasisRows[rowIdx]?.emphasis2 : false;
	const emphasisSide: "left" | "right" = isSummaryRowRight ? "right" : "left";
	const currentEmphasis = emphasisSide === "left" ? currentEmphasisLeft : currentEmphasisRight;

	const patch = (p: ElStyle) => actions.setElStyle(selEl, p);

	const contrastWarn = cur.color && Math.abs((cur.bg ? lum(cur.bg) : 1) - lum(cur.color)) < 0.3;

	return (
		<>
			<SecHead>
				Phần tử <SecPill>{elName}</SecPill>
			</SecHead>
			<SecSub>Chỉnh riêng phần tử này. Bấm vùng trống của khối để chỉnh cả khối.</SecSub>

			{isRaw && (
				<button
					type="button"
					style={{
						display: "flex",
						alignItems: "center",
						gap: 6,
						width: "100%",
						padding: "8px 10px",
						border: "1px solid #fecaca",
						borderRadius: 8,
						background: "#fff",
						cursor: "pointer",
						fontSize: 12.5,
						fontWeight: 600,
						color: "#dc2626",
						marginBottom: 8,
					}}
					onClick={() => {
						actions.delRow(parts[0], rowIdx);
						actions.setSelEl(null);
					}}>
					<i
						className="material-icons"
						style={{ fontSize: 15 }}>
						delete
					</i>
					Xoá tiêu đề mục
				</button>
			)}

			{isSummaryRow && (
				<SegField
					label="Bố cục dòng này"
					options={[
						{ value: "stacked", label: "Nhãn trên / dưới" },
						{ value: "inline", label: "Cùng một hàng" },
					]}
					value={rowLayout}
					onChange={(v) => {
						actions.setRowInline(parts[0], Number(parts[2]), v === "inline");
					}}
				/>
			)}

			{(isSummaryRow || isSummaryRowRight) && (
				<SegField
					label="To (đậm)"
					options={[
						{ value: "0", label: "Tắt" },
						{ value: "1", label: "Bật" },
					]}
					value={currentEmphasis ? "1" : "0"}
					onChange={(v) => {
						actions.toggleRowEmphasis(parts[0], rowIdx, emphasisSide);
					}}
				/>
			)}

			<RangeField
				label="Cỡ chữ"
				value={cur.size ?? defSize}
				min={8}
				max={44}
				onChange={(v) => patch({ size: v })}
			/>

			<SwatchField
				label="Màu chữ"
				presets={COLOR_PRESETS}
				value={cur.color || ""}
				onChange={(v) => patch({ color: v })}
			/>

			<SegField
				label="Độ đậm"
				options={[
					{ value: "0", label: "Tắt" },
					{ value: "1", label: "Bật" },
				]}
				value={cur.bold ? "1" : "0"}
				onChange={(v) => patch({ bold: v === "1" })}
				/>

				<SegField
					label="Viết hoa chữ đầu"
					options={[
						{ value: "0", label: "Tắt" },
						{ value: "1", label: "Bật" },
					]}
					value={cur.capitalize ? "1" : "0"}
					onChange={(v) => patch({ capitalize: v === "1" })}
			/>

			<SegField
				label="Căn lề"
				options={[
					{ value: "left", label: "Trái" },
					{ value: "center", label: "Giữa" },
					{ value: "right", label: "Phải" },
				]}
				value={cur.align || "left"}
				onChange={(v) => patch({ align: v })}
			/>

			{isPill && (
				<>
					<SwatchField
						label="Màu nền"
						presets={BG_PRESETS}
						value={cur.bg || ""}
						onChange={(v) => patch({ bg: v })}
					/>
					<RangeField
						label="Bo góc"
						value={cur.radius ?? 20}
						min={0}
						max={40}
						onChange={(v) => patch({ radius: v })}
					/>
				</>
			)}

			{contrastWarn && (
				<WarnBox>Màu chữ và nền tương phản thấp — khó đọc. Bỏ chọn màu chữ hoặc chọn màu khác.</WarnBox>
			)}
		</>
	);
}

// ---------------------------------------------------------------------------
// BlockPanel
// ---------------------------------------------------------------------------

interface BlockPanelProps {
	block: Block;
	actions: EmailBuilderActions;
}

export function BlockPanel({ block, actions }: BlockPanelProps): JSX.Element {
	const s = block.style ?? {};
	const patch = (p: Block["style"]) => actions.setBlockStyle(block.id!, { ...s, ...p });

	return (
		<>
			<SecHead>
				{block.type} {block.required && <SecPill>bắt buộc</SecPill>}
			</SecHead>
			<SecSub>Bấm vào từng chữ trong khối để chỉnh riêng. Dưới đây là tùy chọn cả khối.</SecSub>

			{block.type === "summary" && (
				<>
					<SegField
						label="Bố cục dòng"
						options={[
							{ value: "stacked", label: "Trên–dưới" },
							{ value: "inline", label: "Ngang hàng" },
						]}
						value={s.layout || "stacked"}
						onChange={(v) => patch({ layout: v as NonNullable<Block["style"]>["layout"] })}
					/>
					<RangeField
						label="Bo góc hộp"
						value={s.radius ?? 12}
						min={0}
						max={24}
						onChange={(v) => patch({ radius: v })}
					/>
					<SegField
						label="Khoảng cách"
						options={[
							{ value: "compact", label: "Hẹp" },
							{ value: "comfortable", label: "Vừa" },
							{ value: "spacious", label: "Rộng" },
						]}
						value={s.density || "comfortable"}
						onChange={(v) => patch({ density: v as NonNullable<Block["style"]>["density"] })}
					/>
				</>
			)}

			{block.type === "notes" && (
				<>
					<SegField
						label="Màu hộp"
						options={[
							{ value: "info", label: "Xanh" },
							{ value: "warning", label: "Vàng" },
							{ value: "neutral", label: "Xám" },
						]}
						value={s.color || "info"}
						onChange={(v) => patch({ color: v as NonNullable<Block["style"]>["color"] })}
					/>
					<SegField
						label="Kiểu hiển thị"
						options={[
							{ value: "list", label: "Gạch đầu dòng" },
							{ value: "rows", label: "2 cột" },
						]}
						value={s.layout || "list"}
						onChange={(v) => patch({ layout: v as NonNullable<Block["style"]>["layout"] })}
					/>
				</>
			)}

			{block.type === "header" && (
				<SecSub>Header tự dùng Màu thương hiệu — đổi ở mục Theme bên dưới để nhuộm cả email.</SecSub>
			)}
		</>
	);
}

// ---------------------------------------------------------------------------
// ThemePanel
// ---------------------------------------------------------------------------

interface ThemePanelProps {
	theme: Theme;
	actions: EmailBuilderActions;
}

export function ThemePanel({ theme, actions }: ThemePanelProps): JSX.Element {
	const brandLight = lum(theme.brand) > 0.6;

	return (
		<>
			<SecHead>Theme chung</SecHead>
			<ThemeCard>
				<FontField
					label="Font chữ (toàn hệ thống)"
					value={theme.font}
					onChange={(v) => actions.setTheme({ font: v })}
				/>
				<SwatchField
					label="Màu thương hiệu"
					presets={BRAND_PRESETS}
					value={theme.brand}
					onChange={(v) => actions.setTheme({ brand: v })}
				/>
				{brandLight && (
					<WarnBox>
						Màu thương hiệu khá sáng — chữ nhấn mạnh, link và chip trên nền trắng có thể khó đọc.
					</WarnBox>
				)}
				<RangeField
					label="Bo góc hộp (toàn cục)"
					value={theme.radius}
					min={0}
					max={20}
					onChange={(v) => actions.setTheme({ radius: v })}
				/>
			</ThemeCard>
		</>
	);
}
